import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from 'recharts';
import { useLanguage } from '../LanguageContext';
import { useAuth } from '../AuthContext';
import { can } from '../permissions';
import {
  brokerMonitor, brokersList, brokerCreate, brokerUpdate, brokerDelete,
  brokerUsersList, brokerUserCreate, brokerUserUpdate, brokerUserDelete,
} from '../api';

const PERIODS = [
  ['today', 'اليوم', 'Today'],
  ['yesterday', 'أمس', 'Yesterday'],
  ['week', 'الأسبوع', 'Week'],
  ['month', 'الشهر', 'Month'],
  ['all', 'الكل', 'All'],
];

// Admin/manager dashboard: monitor every broker company and its team performance.
export default function BrokerMonitor() {
  const { lang, isRTL } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const ar = lang === 'ar';
  const t = (a, e) => (ar ? a : e);
  // Open the leads list filtered to one broker company's leads.
  const openBrokerLeads = (company) => navigate(`/leads?broker=${encodeURIComponent(company)}`);
  const [view, setView] = useState('performance'); // 'performance' | 'manage'
  const canManage = can(user, 'brokers.create') || can(user, 'brokers.edit') || can(user, 'brokers.delete');
  const [period, setPeriod] = useState('month');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [expanded, setExpanded] = useState({}); // company -> bool

  const load = useCallback(async () => {
    setLoading(true);
    setFailed(false);
    const res = await brokerMonitor({ period });
    if (res.error) { setData(null); setFailed(true); } else { setData(res); }
    setLoading(false);
  }, [period]);
  useEffect(() => { load(); }, [load]);

  const fmt = (n) => Number(n || 0).toLocaleString('en-US');
  const money = (n) => Number(n || 0).toLocaleString('en-US', { maximumFractionDigits: 0 });
  const respLabel = (h, sample) => {
    if (!sample) return '—';
    if (h >= 48) return `${(h / 24).toFixed(1)}${ar ? ' يوم' : 'd'}`;
    return `${h.toFixed(1)}${ar ? ' س' : 'h'}`;
  };
  const agoLabel = (ts) => {
    if (!ts) return '—';
    const days = Math.floor((Date.now() - new Date(ts).getTime()) / 86400000);
    if (days <= 0) return t('اليوم', 'Today');
    if (days === 1) return t('أمس', '1d ago');
    if (days < 30) return t(`قبل ${days} يوم`, `${days}d ago`);
    return String(ts).slice(0, 10);
  };

  const ov = data?.overall || {};
  const brokers = data?.brokers || [];
  const toggle = (c) => setExpanded((e) => ({ ...e, [c]: !e[c] }));

  const kpi = (label, value, color) => (
    <div style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 12, padding: 14, flex: '1 1 120px', minWidth: 120 }}>
      <div style={{ fontSize: '0.74rem', color: 'var(--text-secondary)', marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: '1.4rem', fontWeight: 800, color: color || 'var(--text-primary)' }}>{value}</div>
    </div>
  );
  const cell = { padding: '9px 10px', borderBottom: '1px solid var(--border)', fontSize: '0.84rem', textAlign: 'center', whiteSpace: 'nowrap' };
  const nameCell = { ...cell, textAlign: isRTL ? 'right' : 'left', fontWeight: 600 };
  const subCell = { padding: '7px 10px', borderBottom: '1px solid var(--border)', fontSize: '0.8rem', textAlign: 'center', whiteSpace: 'nowrap' };

  return (
    <div dir={isRTL ? 'rtl' : 'ltr'} style={{ maxWidth: 1180, margin: '0 auto' }}>
      {/* الأداء (monitor) vs إدارة الوسطاء (management) */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap', borderBottom: '1px solid var(--border)', paddingBottom: 12 }}>
        <button onClick={() => setView('performance')} style={{ padding: '7px 16px', borderRadius: 8, border: '1px solid var(--border)', background: view === 'performance' ? 'var(--accent)' : 'var(--bg-secondary)', color: view === 'performance' ? '#fff' : 'var(--text-primary)', fontSize: '0.85rem', fontWeight: 700, cursor: 'pointer' }}>{t('الأداء', 'Performance')}</button>
        {canManage && <button onClick={() => setView('manage')} style={{ padding: '7px 16px', borderRadius: 8, border: '1px solid var(--border)', background: view === 'manage' ? 'var(--accent)' : 'var(--bg-secondary)', color: view === 'manage' ? '#fff' : 'var(--text-primary)', fontSize: '0.85rem', fontWeight: 700, cursor: 'pointer' }}>{t('إدارة الوسطاء', 'Manage brokers')}</button>}
      </div>

      {view === 'manage' ? (
        <BrokerAdmin user={user} />
      ) : (
      <>
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        {PERIODS.map(([k, a, e]) => (
          <button key={k} onClick={() => setPeriod(k)} style={{ padding: '7px 16px', borderRadius: 8, border: '1px solid var(--border)', background: period === k ? 'var(--accent)' : 'var(--bg-secondary)', color: period === k ? '#fff' : 'var(--text-primary)', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer' }}>
            {t(a, e)}
          </button>
        ))}
      </div>

      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-secondary)' }}>...</div>
      ) : failed ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-secondary)' }}>
          {t('تعذّر تحميل البيانات', 'Could not load data')}
          <div style={{ marginTop: 12 }}>
            <button onClick={load} style={{ padding: '7px 18px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontWeight: 600, cursor: 'pointer' }}>
              {t('إعادة المحاولة', 'Retry')}
            </button>
          </div>
        </div>
      ) : brokers.length === 0 ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-secondary)' }}>{t('لا يوجد وسطاء بعد', 'No brokers yet')}</div>
      ) : (
        <>
          {/* KPI overview */}
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 20 }}>
            {kpi(t('الوسطاء', 'Brokers'), fmt(ov.brokers))}
            {kpi(t('فرق نشطة', 'Active teams'), fmt(ov.activeTeams), 'var(--accent)')}
            {kpi(t('الموظفون', 'Members'), fmt(ov.members))}
            {kpi(t('عملاء الفترة', 'Leads (period)'), fmt(ov.total))}
            {kpi(t('محتملون', 'Potential'), fmt(ov.potential), 'var(--accent)')}
            {kpi(t('مكتسب', 'Won'), fmt(ov.won), 'var(--success, #16a34a)')}
            {kpi(t('نسبة التحويل', 'Conversion'), `${(ov.conversionRate || 0).toFixed(1)}%`)}
            {kpi(t('تم العمل عليهم', 'Worked'), fmt(ov.worked), 'var(--accent)')}
            {kpi(t('يحتاج متابعة', 'Needs attention'), fmt(ov.stale), ov.stale > 0 ? 'var(--danger, #dc2626)' : undefined)}
            {kpi(t('صفقات', 'Deals'), fmt(ov.dealsCount), 'var(--success, #16a34a)')}
            {kpi(t('العمولة (ر.س)', 'Commission (SAR)'), money(ov.commission), 'var(--success, #16a34a)')}
          </div>

          {/* Trend across all brokers */}
          <div style={{ fontWeight: 800, fontSize: '0.95rem', margin: '8px 0 10px', color: 'var(--text-primary)' }}>{t('نشاط الوسطاء عبر الوقت', 'Broker activity over time')}</div>
          <div style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 12, padding: 14, marginBottom: 24, height: 260 }}>
            {(data.trend || []).length === 0 ? (
              <div style={{ padding: 30, textAlign: 'center', color: 'var(--text-secondary)' }}>{t('لا توجد بيانات', 'No data')}</div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={data.trend} margin={{ left: isRTL ? 10 : 0, right: isRTL ? 0 : 10, top: 10, bottom: 6 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                  <XAxis dataKey="date" reversed={isRTL} stroke="var(--text-secondary)" tick={{ fill: 'var(--text-secondary)', fontSize: 10 }} tickFormatter={(d) => String(d).slice(5)} />
                  <YAxis orientation={isRTL ? 'right' : 'left'} allowDecimals={false} stroke="var(--text-secondary)" tick={{ fill: 'var(--text-secondary)', fontSize: 10 }} />
                  <Tooltip contentStyle={{ background: 'var(--bg-card, #1e2132)', border: '1px solid var(--border)', borderRadius: 8, fontSize: 12 }} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="leads" name={t('عملاء جدد', 'New leads')} fill="var(--accent, #6366f1)" radius={[3, 3, 0, 0]} />
                  <Line type="monotone" dataKey="won" name={t('مكتسب', 'Won')} stroke="#16a34a" strokeWidth={2} dot={{ r: 2 }} />
                </ComposedChart>
              </ResponsiveContainer>
            )}
          </div>

          {/* Per-broker performance */}
          <div style={{ fontWeight: 800, fontSize: '0.95rem', margin: '8px 0 10px', color: 'var(--text-primary)' }}>
            {t('أداء الوسطاء', 'Broker performance')} <span style={{ fontWeight: 500, fontSize: '0.78rem', color: 'var(--text-secondary)' }}>· {t('اضغط على الوسيط لعرض عملائه، أو على السهم لعرض الفريق', "tap a broker to see its leads, or the arrow for its team")}</span>
          </div>
          <div style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 12, overflowX: 'auto', marginBottom: 24 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 900 }}>
              <thead>
                <tr style={{ background: 'var(--bg-primary)' }}>
                  <th style={nameCell}>{t('الوسيط', 'Broker')}</th>
                  <th style={cell}>{t('الفريق', 'Team')}</th>
                  <th style={cell}>{t('عملاء', 'Leads')}</th>
                  <th style={cell}>{t('تم العمل', 'Worked')}</th>
                  <th style={cell}>{t('محتمل', 'Potential')}</th>
                  <th style={cell}>{t('مكتسب', 'Won')}</th>
                  <th style={cell}>{t('خسارة', 'Lost')}</th>
                  <th style={cell}>{t('التحويل', 'Conv.')}</th>
                  <th style={cell}>{t('صفقات', 'Deals')}</th>
                  <th style={cell}>{t('العمولة', 'Commission')}</th>
                  <th style={cell}>{t('زمن الرد', 'Response')}</th>
                  <th style={cell}>{t('متأخر', 'Stale')}</th>
                  <th style={cell}>{t('آخر نشاط', 'Last active')}</th>
                </tr>
              </thead>
              <tbody>
                {brokers.map((b) => (
                  <React.Fragment key={b.company}>
                    <tr onClick={() => openBrokerLeads(b.company)} title={t('عرض عملاء هذا الوسيط', "View this broker's leads")} style={{ cursor: 'pointer', background: expanded[b.company] ? 'var(--bg-primary)' : undefined }}>
                      <td style={{ ...nameCell, color: 'var(--accent)' }}>
                        <span
                          onClick={(e) => { e.stopPropagation(); if (b.members.length > 0) toggle(b.company); }}
                          title={b.members.length > 0 ? t('عرض الفريق', 'Show team') : ''}
                          style={{ display: 'inline-block', width: 16, color: 'var(--text-secondary)', cursor: b.members.length > 0 ? 'pointer' : 'default' }}
                        >{b.members.length > 0 ? (expanded[b.company] ? '▾' : '▸') : ''}</span>
                        <span style={{ textDecoration: 'underline', textUnderlineOffset: 3 }}>{b.company}</span>
                        {!b.active && <span style={{ marginInlineStart: 6, fontSize: '0.68rem', color: 'var(--text-secondary)' }}>({t('غير نشط', 'inactive')})</span>}
                      </td>
                      <td style={cell} title={t('نشط من الإجمالي', 'active / total')}>{fmt(b.activeMembers)}{b.totalMembers > b.activeMembers ? <span style={{ color: 'var(--text-secondary)' }}>/{fmt(b.totalMembers)}</span> : ''}</td>
                      <td style={cell}>{fmt(b.total)}</td>
                      <td style={{ ...cell, color: 'var(--accent)', fontWeight: 700 }}>{fmt(b.worked)}</td>
                      <td style={cell}>{fmt(b.potential)}</td>
                      <td style={{ ...cell, color: 'var(--success, #16a34a)', fontWeight: 700 }}>{fmt(b.won)}</td>
                      <td style={{ ...cell, color: 'var(--danger, #dc2626)' }}>{fmt(b.lost)}</td>
                      <td style={cell}>{(b.conversionRate || 0).toFixed(0)}%</td>
                      <td style={cell}>{fmt(b.deals.count)}</td>
                      <td style={{ ...cell, color: 'var(--success, #16a34a)', fontWeight: 700 }}>{money(b.deals.commission)}</td>
                      <td style={cell}>{respLabel(b.avgResponseHours, b.responseSample)}</td>
                      <td style={{ ...cell, color: b.stale > 0 ? 'var(--danger, #dc2626)' : 'var(--text-secondary)', fontWeight: b.stale > 0 ? 700 : 400 }}>{fmt(b.stale)}</td>
                      <td style={{ ...cell, color: 'var(--text-secondary)' }}>{agoLabel(b.lastActivity)}</td>
                    </tr>
                    {expanded[b.company] && (
                      <tr>
                        <td colSpan={13} style={{ padding: 0, background: 'var(--bg-primary)' }}>
                          <div style={{ padding: '4px 14px 12px' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                              <thead>
                                <tr>
                                  <th style={{ ...subCell, textAlign: isRTL ? 'right' : 'left', color: 'var(--text-secondary)' }}>{t('الموظف', 'Member')}</th>
                                  <th style={{ ...subCell, color: 'var(--text-secondary)' }}>{t('عملاء', 'Leads')}</th>
                                  <th style={{ ...subCell, color: 'var(--text-secondary)' }}>{t('محتمل', 'Potential')}</th>
                                  <th style={{ ...subCell, color: 'var(--text-secondary)' }}>{t('مكتسب', 'Won')}</th>
                                  <th style={{ ...subCell, color: 'var(--text-secondary)' }}>{t('خسارة', 'Lost')}</th>
                                  <th style={{ ...subCell, color: 'var(--text-secondary)' }}>{t('التحويل', 'Conv.')}</th>
                                </tr>
                              </thead>
                              <tbody>
                                {b.members.length === 0 ? (
                                  <tr><td colSpan={6} style={{ ...subCell, textAlign: 'center', color: 'var(--text-secondary)' }}>{t('لا نشاط في الفترة', 'No activity in period')}</td></tr>
                                ) : b.members.map((m) => (
                                  <tr key={m.name}>
                                    <td style={{ ...subCell, textAlign: isRTL ? 'right' : 'left', fontWeight: 600 }}>{m.name}</td>
                                    <td style={subCell}>{fmt(m.total)}</td>
                                    <td style={subCell}>{fmt(m.potential)}</td>
                                    <td style={{ ...subCell, color: 'var(--success, #16a34a)', fontWeight: 700 }}>{fmt(m.won)}</td>
                                    <td style={{ ...subCell, color: 'var(--danger, #dc2626)' }}>{fmt(m.lost)}</td>
                                    <td style={subCell}>{(m.conversionRate || 0).toFixed(0)}%</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
      </>
      )}
    </div>
  );
}

// ── Broker-company management: list + add + edit + enable/disable + delete ──
// Rendered under the الوسطاء tab. Each action is gated by the matching brokers.*
// permission; a broker still referenced by deals/leads can only be disabled.
function BrokerAdmin({ user }) {
  const { lang, isRTL } = useLanguage();
  const ar = lang === 'ar';
  const t = (a, e) => (ar ? a : e);
  const canCreate = can(user, 'brokers.create');
  const canEdit = can(user, 'brokers.edit');
  const canDelete = can(user, 'brokers.delete');

  const blank = { name: '', type: 'company', comm_type: 'percentage', default_pct: '2', default_amt: '0' };
  const [list, setList] = useState(null);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [addForm, setAddForm] = useState(blank);
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState(blank);
  const [usersFor, setUsersFor] = useState(null); // broker id whose users panel is open

  const load = useCallback(async () => {
    setLoading(true);
    const res = await brokersList();
    setList(res.error ? [] : (res.brokers || []));
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const inp = { padding: '7px 9px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-primary)', color: 'var(--text-primary)', fontSize: '0.82rem' };
  const btn = (bg) => ({ padding: '6px 12px', borderRadius: 8, border: '1px solid var(--border)', background: bg || 'var(--bg-secondary)', color: bg ? '#fff' : 'var(--text-primary)', fontSize: '0.78rem', fontWeight: 700, cursor: 'pointer' });
  const cell = { padding: '9px 10px', borderBottom: '1px solid var(--border)', fontSize: '0.83rem', textAlign: 'center', whiteSpace: 'nowrap' };
  const nameCell = { ...cell, textAlign: isRTL ? 'right' : 'left', fontWeight: 600 };
  const commLabel = (b) => b.comm_type === 'fixed' ? `${Number(b.default_amt || 0).toLocaleString('en-US')} ${t('ر.س', 'SAR')}` : `${Number(b.default_pct || 0)}%`;

  const doAdd = async () => {
    if (!addForm.name.trim()) { setMsg({ type: 'error', text: t('الاسم مطلوب', 'Name is required') }); return; }
    setBusy(true);
    const res = await brokerCreate({ name: addForm.name.trim(), type: addForm.type, comm_type: addForm.comm_type, default_pct: Number(addForm.default_pct) || 0, default_amt: Number(addForm.default_amt) || 0 });
    setBusy(false);
    if (res.error) { setMsg({ type: 'error', text: res.error === 'name_exists' ? t('الاسم مستخدم مسبقًا', 'Name already exists') : t('تعذّر الإضافة', 'Could not add') }); return; }
    setMsg({ type: 'success', text: t('تمت إضافة الوسيط', 'Broker added') }); setAddForm(blank); setShowAdd(false); load();
  };
  const startEdit = (b) => { setMsg(null); setEditId(b.id); setEditForm({ name: b.name, type: b.type || 'company', comm_type: b.comm_type || 'percentage', default_pct: String(b.default_pct ?? 2), default_amt: String(b.default_amt ?? 0) }); };
  const saveEdit = async () => {
    if (!editForm.name.trim()) { setMsg({ type: 'error', text: t('الاسم مطلوب', 'Name is required') }); return; }
    setBusy(true);
    const res = await brokerUpdate(editId, { name: editForm.name.trim(), type: editForm.type, comm_type: editForm.comm_type, default_pct: Number(editForm.default_pct) || 0, default_amt: Number(editForm.default_amt) || 0 });
    setBusy(false);
    if (res.error) { setMsg({ type: 'error', text: res.error === 'name_exists' ? t('الاسم مستخدم مسبقًا', 'Name already exists') : t('تعذّر الحفظ', 'Could not save') }); return; }
    setEditId(null); load();
  };
  const toggleActive = async (b) => { setBusy(true); await brokerUpdate(b.id, { active: b.active ? 0 : 1 }); setBusy(false); load(); };
  const doDelete = async (b) => {
    if (!window.confirm(t(`حذف «${b.name}» نهائيًا؟`, `Permanently delete "${b.name}"?`))) return;
    setBusy(true);
    const res = await brokerDelete(b.id);
    setBusy(false);
    if (res.error === 'in_use') { setMsg({ type: 'error', text: t(`لا يمكن الحذف — مرتبط بـ ${res.deals || 0} صفقة و${res.leads || 0} عميل. عطّله بدلاً من الحذف.`, `Can't delete — linked to ${res.deals || 0} deals and ${res.leads || 0} leads. Disable it instead.`) }); return; }
    if (res.error) { setMsg({ type: 'error', text: t('تعذّر الحذف', 'Could not delete') }); return; }
    setMsg({ type: 'success', text: t('تم حذف الوسيط', 'Broker deleted') }); load();
  };

  const list0 = list || [];
  return (
    <div dir={isRTL ? 'rtl' : 'ltr'}>
      {msg && (
        <div style={{ background: msg.type === 'error' ? 'rgba(239,68,68,0.1)' : 'rgba(34,197,94,0.1)', border: `1px solid ${msg.type === 'error' ? 'rgba(239,68,68,0.3)' : 'rgba(34,197,94,0.3)'}`, borderRadius: 10, padding: '10px 14px', marginBottom: 14, fontSize: '0.85rem', color: msg.type === 'error' ? 'var(--error, #dc2626)' : 'var(--success, #16a34a)' }}>{msg.text}</div>
      )}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
        <div style={{ fontWeight: 800, fontSize: '0.95rem' }}>{t('شركات الوسطاء', 'Broker companies')} <span style={{ fontWeight: 500, fontSize: '0.78rem', color: 'var(--text-secondary)' }}>({list0.length})</span></div>
        {canCreate && !showAdd && <button onClick={() => { setShowAdd(true); setMsg(null); }} style={btn('var(--accent)')}>+ {t('إضافة وسيط', 'Add broker')}</button>}
      </div>

      {canCreate && showAdd && (
        <div style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 12, padding: 14, marginBottom: 16, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <input placeholder={t('اسم الوسيط', 'Broker name')} value={addForm.name} onChange={e => setAddForm(p => ({ ...p, name: e.target.value }))} style={{ ...inp, flex: '2 1 180px' }} />
          <select value={addForm.type} onChange={e => setAddForm(p => ({ ...p, type: e.target.value }))} style={inp}>
            <option value="company">{t('شركة', 'Company')}</option>
            <option value="individual">{t('فرد', 'Individual')}</option>
          </select>
          <select value={addForm.comm_type} onChange={e => setAddForm(p => ({ ...p, comm_type: e.target.value }))} style={inp}>
            <option value="percentage">{t('نسبة %', 'Percentage %')}</option>
            <option value="fixed">{t('مبلغ ثابت', 'Fixed amount')}</option>
          </select>
          {addForm.comm_type === 'percentage'
            ? <input type="number" step="0.1" placeholder="%" value={addForm.default_pct} onChange={e => setAddForm(p => ({ ...p, default_pct: e.target.value }))} style={{ ...inp, width: 90 }} />
            : <input type="number" placeholder={t('ر.س', 'SAR')} value={addForm.default_amt} onChange={e => setAddForm(p => ({ ...p, default_amt: e.target.value }))} style={{ ...inp, width: 110 }} />}
          <button disabled={busy} onClick={doAdd} style={btn('var(--accent)')}>{t('حفظ', 'Save')}</button>
          <button disabled={busy} onClick={() => { setShowAdd(false); setAddForm(blank); }} style={btn()}>{t('إلغاء', 'Cancel')}</button>
        </div>
      )}

      <div style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 12, overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 720 }}>
          <thead>
            <tr style={{ background: 'var(--bg-primary)' }}>
              <th style={nameCell}>{t('الوسيط', 'Broker')}</th>
              <th style={cell}>{t('النوع', 'Type')}</th>
              <th style={cell}>{t('العمولة', 'Commission')}</th>
              <th style={cell}>{t('الحالة', 'Status')}</th>
              <th style={cell}>{t('الاستخدام', 'Usage')}</th>
              {(canCreate || canEdit || canDelete) && <th style={cell}>{t('إجراءات', 'Actions')}</th>}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} style={{ ...cell, textAlign: 'center', color: 'var(--text-secondary)' }}>...</td></tr>
            ) : list0.length === 0 ? (
              <tr><td colSpan={6} style={{ ...cell, textAlign: 'center', color: 'var(--text-secondary)' }}>{t('لا يوجد وسطاء بعد — أضِف أول وسيط', 'No brokers yet — add the first one')}</td></tr>
            ) : list0.map(b => (
              editId === b.id ? (
                <tr key={b.id} style={{ background: 'var(--bg-primary)' }}>
                  <td style={nameCell}><input value={editForm.name} onChange={e => setEditForm(p => ({ ...p, name: e.target.value }))} style={{ ...inp, width: '95%' }} /></td>
                  <td style={cell}>
                    <select value={editForm.type} onChange={e => setEditForm(p => ({ ...p, type: e.target.value }))} style={inp}>
                      <option value="company">{t('شركة', 'Company')}</option>
                      <option value="individual">{t('فرد', 'Individual')}</option>
                    </select>
                  </td>
                  <td style={cell}>
                    <div style={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
                      <select value={editForm.comm_type} onChange={e => setEditForm(p => ({ ...p, comm_type: e.target.value }))} style={inp}>
                        <option value="percentage">%</option>
                        <option value="fixed">{t('ثابت', 'Fixed')}</option>
                      </select>
                      {editForm.comm_type === 'percentage'
                        ? <input type="number" step="0.1" value={editForm.default_pct} onChange={e => setEditForm(p => ({ ...p, default_pct: e.target.value }))} style={{ ...inp, width: 70 }} />
                        : <input type="number" value={editForm.default_amt} onChange={e => setEditForm(p => ({ ...p, default_amt: e.target.value }))} style={{ ...inp, width: 90 }} />}
                    </div>
                  </td>
                  <td style={cell}>{b.active ? t('نشط', 'Active') : t('معطّل', 'Disabled')}</td>
                  <td style={{ ...cell, color: 'var(--text-secondary)' }}>—</td>
                  <td style={cell}>
                    <button disabled={busy} onClick={saveEdit} style={btn('var(--accent)')}>{t('حفظ', 'Save')}</button>
                    <button disabled={busy} onClick={() => setEditId(null)} style={{ ...btn(), marginInlineStart: 6 }}>{t('إلغاء', 'Cancel')}</button>
                  </td>
                </tr>
              ) : (
                <React.Fragment key={b.id}>
                <tr style={{ opacity: b.active ? 1 : 0.55, background: usersFor === b.id ? 'var(--bg-primary)' : undefined }}>
                  <td style={nameCell}>{b.name}</td>
                  <td style={cell}>{b.type === 'individual' ? t('فرد', 'Individual') : t('شركة', 'Company')}</td>
                  <td style={cell}>{commLabel(b)}</td>
                  <td style={cell}>
                    <span style={{ padding: '2px 8px', borderRadius: 7, fontSize: '0.68rem', fontWeight: 700, background: (b.active ? '#16a34a' : '#dc2626') + '18', color: b.active ? '#16a34a' : '#dc2626' }}>{b.active ? t('نشط', 'Active') : t('معطّل', 'Disabled')}</span>
                  </td>
                  <td style={{ ...cell, color: 'var(--text-secondary)', fontSize: '0.75rem' }}>{b.deals} {t('صفقة', 'deals')} · {b.leads} {t('عميل', 'leads')}</td>
                  {(canCreate || canEdit || canDelete) && (
                    <td style={cell}>
                      <button disabled={busy} onClick={() => setUsersFor(usersFor === b.id ? null : b.id)} style={{ ...btn(usersFor === b.id ? 'var(--accent)' : undefined) }}>{usersFor === b.id ? '▾ ' : ''}{t('المستخدمون', 'Users')}</button>
                      {canEdit && <button disabled={busy} onClick={() => startEdit(b)} style={{ ...btn(), marginInlineStart: 6 }}>{t('تعديل', 'Edit')}</button>}
                      {canEdit && <button disabled={busy} onClick={() => toggleActive(b)} style={{ ...btn(), marginInlineStart: 6 }}>{b.active ? t('تعطيل', 'Disable') : t('تفعيل', 'Enable')}</button>}
                      {canDelete && <button disabled={busy} onClick={() => doDelete(b)} style={{ ...btn(), marginInlineStart: 6, color: '#dc2626', borderColor: '#dc262655' }}>{t('حذف', 'Delete')}</button>}
                    </td>
                  )}
                </tr>
                {usersFor === b.id && (
                  <tr>
                    <td colSpan={6} style={{ padding: 0, background: 'var(--bg-primary)' }}>
                      <BrokerUsersPanel brokerId={b.id} brokerName={b.name} canCreate={canCreate} canEdit={canEdit} canDelete={canDelete} t={t} isRTL={isRTL} />
                    </td>
                  </tr>
                )}
                </React.Fragment>
              )
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── A broker company's login accounts (owner + employees), managed by an admin ──
// Rendered inline under a broker row in BrokerAdmin. The first account added
// becomes the company OWNER; later ones are employees (same rule as broker.js).
function BrokerUsersPanel({ brokerId, brokerName, canCreate, canEdit, canDelete, t, isRTL }) {
  const [users, setUsers] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: '', email: '', password: '' });
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const res = await brokerUsersList(brokerId);
    setUsers(res.error ? [] : (res.users || []));
    setLoading(false);
  }, [brokerId]);
  useEffect(() => { load(); }, [load]);

  const errText = (code) => ({
    name_and_email_required: t('الاسم والبريد مطلوبان', 'Name and email are required'),
    weak_password: t('كلمة المرور 8 أحرف على الأقل وتحتوي حروفاً وأرقاماً', 'Password must be 8+ chars with letters and numbers'),
    email_exists: t('البريد مستخدم مسبقًا', 'That email already exists'),
    not_found: t('غير موجود', 'Not found'),
  }[code] || t('حدث خطأ، حاول مرة أخرى', 'Something went wrong, try again'));

  const add = async () => {
    setMsg(null);
    if (!form.name.trim() || !form.email.trim()) { setMsg({ type: 'error', text: errText('name_and_email_required') }); return; }
    setBusy(true);
    const res = await brokerUserCreate(brokerId, { name: form.name.trim(), email: form.email.trim(), password: form.password });
    setBusy(false);
    if (res.error) { setMsg({ type: 'error', text: errText(res.error) }); return; }
    setForm({ name: '', email: '', password: '' });
    setMsg({ type: 'success', text: t('تمت إضافة المستخدم', 'User added') });
    load();
  };
  const toggle = async (u) => { setBusy(true); await brokerUserUpdate(brokerId, u.id, { active: u.active ? 0 : 1 }); setBusy(false); load(); };
  const resetPw = async (u) => {
    const pw = window.prompt(t(`كلمة مرور جديدة لـ ${u.name} (8 أحرف+، حروف وأرقام)`, `New password for ${u.name} (8+ chars, letters & numbers)`));
    if (!pw) return;
    const res = await brokerUserUpdate(brokerId, u.id, { password: pw });
    window.alert(res.error ? errText(res.error) : t('تم تحديث كلمة المرور', 'Password updated'));
  };
  const remove = async (u) => {
    if (!window.confirm(t(`حذف ${u.name} نهائيًا؟ ستُلغى إحالة عملائه.`, `Delete ${u.name}? Their leads will be unassigned.`))) return;
    const res = await brokerUserDelete(brokerId, u.id);
    if (res.error) { window.alert(errText(res.error)); return; }
    load();
  };

  const inp = { padding: '7px 9px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: '0.82rem' };
  const sbtn = { fontSize: '0.74rem', marginInlineEnd: 6, padding: '4px 9px', borderRadius: 6, border: '1px solid var(--border)', background: 'none', color: 'var(--text-secondary)', cursor: 'pointer', whiteSpace: 'nowrap' };
  const ucell = { padding: '7px 10px', borderBottom: '1px solid var(--border)', fontSize: '0.8rem', textAlign: isRTL ? 'right' : 'left', whiteSpace: 'nowrap' };
  const list0 = users || [];

  return (
    <div style={{ padding: '6px 14px 14px' }}>
      <div style={{ fontWeight: 700, fontSize: '0.85rem', margin: '4px 0 10px' }}>
        {t('مستخدمو', 'Users of')} {brokerName} <span style={{ fontWeight: 500, color: 'var(--text-secondary)', fontSize: '0.75rem' }}>({list0.length})</span>
      </div>
      {msg && (
        <div style={{ background: msg.type === 'error' ? 'rgba(239,68,68,0.1)' : 'rgba(34,197,94,0.1)', border: `1px solid ${msg.type === 'error' ? 'rgba(239,68,68,0.3)' : 'rgba(34,197,94,0.3)'}`, borderRadius: 8, padding: '7px 12px', marginBottom: 10, fontSize: '0.82rem', color: msg.type === 'error' ? 'var(--error, #dc2626)' : 'var(--success, #16a34a)' }}>{msg.text}</div>
      )}
      {canCreate && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12, alignItems: 'center' }}>
          <input placeholder={t('الاسم', 'Name')} value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} style={{ ...inp, flex: '1 1 140px' }} />
          <input placeholder={t('البريد الإلكتروني', 'Email')} value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} style={{ ...inp, flex: '1 1 160px', direction: 'ltr' }} />
          <input type="password" placeholder={t('كلمة المرور', 'Password')} value={form.password} onChange={e => setForm(p => ({ ...p, password: e.target.value }))} style={{ ...inp, flex: '1 1 140px', direction: 'ltr' }} />
          <button disabled={busy} onClick={add} style={{ padding: '7px 14px', borderRadius: 8, border: 'none', background: 'var(--accent)', color: '#fff', fontWeight: 700, fontSize: '0.8rem', cursor: busy ? 'not-allowed' : 'pointer', opacity: busy ? 0.6 : 1 }}>{t('إضافة', 'Add')}</button>
        </div>
      )}
      {loading ? (
        <div style={{ padding: 14, color: 'var(--text-secondary)', fontSize: '0.82rem' }}>...</div>
      ) : list0.length === 0 ? (
        <div style={{ padding: 14, color: 'var(--text-secondary)', fontSize: '0.82rem' }}>{t('لا يوجد مستخدمون بعد', 'No users yet')}</div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={{ ...ucell, color: 'var(--text-secondary)' }}>{t('الاسم', 'Name')}</th>
              <th style={{ ...ucell, color: 'var(--text-secondary)' }}>{t('البريد', 'Email')}</th>
              <th style={{ ...ucell, color: 'var(--text-secondary)' }}>{t('الحالة', 'Status')}</th>
              {(canEdit || canDelete) && <th style={{ ...ucell, color: 'var(--text-secondary)' }}></th>}
            </tr>
          </thead>
          <tbody>
            {list0.map(u => (
              <tr key={u.id}>
                <td style={ucell}>{u.name}{u.broker_owner ? ` · ${t('المالك', 'Owner')}` : ''}</td>
                <td style={{ ...ucell, direction: 'ltr', textAlign: isRTL ? 'right' : 'left' }}>{u.email}</td>
                <td style={ucell}><span style={{ color: u.active ? 'var(--success, #16a34a)' : 'var(--text-secondary)' }}>{u.active ? t('نشط', 'Active') : t('معطّل', 'Inactive')}</span></td>
                {(canEdit || canDelete) && (
                  <td style={{ ...ucell, whiteSpace: 'nowrap' }}>
                    {canEdit && <button disabled={busy} onClick={() => toggle(u)} style={sbtn}>{u.active ? t('تعطيل', 'Deactivate') : t('تفعيل', 'Activate')}</button>}
                    {canEdit && <button disabled={busy} onClick={() => resetPw(u)} style={sbtn}>{t('كلمة المرور', 'Password')}</button>}
                    {canDelete && <button disabled={busy} onClick={() => remove(u)} style={{ ...sbtn, color: '#ef4444', border: '1px solid rgba(239,68,68,0.45)' }}>{t('حذف', 'Delete')}</button>}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
